<template>
  <WdsPageSignup
    logo-link=""
    :nav-prev="showPrevButton"
    :nav-next="showNextButton"
    @prev="handlePreviousScreen"
    @next="handleNextScreen"
  >
    <template #prev-text>
      <span>{{ $t('Previous') }}</span>
    </template>

    <template #next-text>
      <span>{{ $t('Next') }}</span>
    </template>

    <template #left>
      <div v-if="brandLogoSrc" class="signup-cobrand">
        <img :src="brandLogoSrc" :alt="brandName" class="signup-cobrand-logo" />
        <div class="signup-cobrand-powered">
          powered by
          <WdsIcon :size="18" name="worksome" />
          <span class="signup-cobrand-name">worksome</span>
        </div>
      </div>
      <slot name="left">
        <slot name="heading">
          <FlowHeading
            :title="title"
            :sub-title="subTitle"
            :title-type="titleType"
            class="flow-heading"
          />
        </slot>

        <WdsProgress
          :steps="steps"
          :current="currentStep"
          :most-completed-step="mostCompletedStep"
          @update-step="gotoStep"
        />
      </slot>
    </template>

    <template #left-footer>
      <WdsButtonText href="/logout">
        {{ $t('Log out') }}
      </WdsButtonText>
    </template>

    <template #right>
      <slot name="screen" />
    </template>
  </WdsPageSignup>
</template>

<script>
import gql from 'graphql-tag'
import { WdsButtonText, WdsIcon, WdsPageSignup, WdsProgress } from '@worksome/wds-components'
import FlowHeading from '@/components/flows/FlowHeading.vue'
import FlowCoordinatePath from '@/utils/flows/FlowCoordinatePath'

export default {
  name: 'SignupFlowLayout',

  components: {
    WdsPageSignup,
    WdsProgress,
    WdsButtonText,
    WdsIcon,
    FlowHeading,
  },

  provide() {
    return {
      previous: this.handlePreviousScreen,
      next: this.handleNextScreen,
      skip: this.handleSkipScreen,
      refreshFlowCoordinates: this.refreshFlowCoordinates,
    }
  },

  props: {
    brandLogoSrc: {
      type: String,
      default: '',
    },
    brandName: {
      type: String,
      default: '',
    },
    title: {
      type: String,
      default: '',
    },
    subTitle: {
      type: String,
      default: '',
    },
    titleType: {
      type: String,
      default: 'title3',
      validator(value) {
        return ['display', 'subtitle', 'title1', 'title2', 'title3', 'title4'].includes(value)
      },
    },
  },

  data() {
    return {
      steps: [],
      currentStep: [],
      mostCompletedStep: null,
      coordinates: {},
    }
  },

  computed: {
    isFinalStep() {
      let finalStep = this.steps[this.steps.length - 1]

      if (finalStep?.children?.length) {
        finalStep = finalStep.children[finalStep.children.length - 1]
      }

      return finalStep?.url === this.coordinates?.current?.url
    },
    showPrevButton() {
      return !!this.coordinates.previous
    },
    // conditions: if next url is valid and
    // if it is not the final step
    // if it is not current screen to prevent folks from jumping required screens JIRA-6568
    showNextButton() {
      const isCompletedStepAtLeastCurrent =
        FlowCoordinatePath.compare(this.mostCompletedStep, this.currentStep) >= 0

      return !!this.coordinates.next && !this.isFinalStep && isCompletedStepAtLeastCurrent
    },
  },

  methods: {
    handlePreviousScreen() {
      window.location = `${window.location.protocol}//${window.location.host}${this.coordinates.previous}`
    },
    handleNextScreen() {
      window.location = `${window.location.protocol}//${window.location.host}${this.coordinates.next}`
    },
    handleSkipScreen() {
      window.location = this.isFinalStep
        ? `${window.location.protocol}//${window.location.host}/dashboard?utm_nooverride=1'`
        : `${window.location.protocol}//${window.location.host}${this.coordinates.next}`
    },
    gotoStep(step) {
      window.location = step.url
    },
    refreshFlowCoordinates() {
      this.$apollo.queries.getFlowCoordinates.refetch()
    },
  },

  apollo: {
    getFlowCoordinates: {
      query: gql`
        query FlowCoordinates($path: String!) {
          flowCoordinate(input: { path: $path }) {
            previous
            next
            mostCompletedStep
            current {
              path
              url
            }
            navigation {
              name
              url
              isCurrent
              children {
                name
                url
                isCurrent
                children {
                  name
                  url
                  isCurrent
                }
              }
            }
          }
        }
      `,
      variables() {
        return {
          path: `${window.location.pathname}${window.location.search}`,
        }
      },
      result({ data }) {
        this.coordinates = data.flowCoordinate
        this.steps = data.flowCoordinate?.navigation?.children ?? []
        this.currentStep = data.flowCoordinate?.current.path ?? []

        this.mostCompletedStep = data.flowCoordinate?.mostCompletedStep
      },
      update: (data) => data.flowCoordinate,
    },
  },
}
</script>

<style scoped>
.flow-heading {
  @apply tw-hidden;
  @apply md:tw-block;
}

.signup-cobrand {
  /* match WdsPageSignup's original logo position (absolute, top-left) so the heading isn't pushed down */
  position: absolute;
  top: 1.75rem;
  left: 2rem;
}

.signup-cobrand-logo {
  display: block;
  height: 18px;
  width: auto;
  max-width: 100%;
}

.signup-cobrand-powered {
  display: flex;
  align-items: center;
  gap: 5px;
  margin-top: 6px;
  font-size: 11px;
  color: #8d8c92;
}

.signup-cobrand-name {
  font-weight: 400;
}
</style>
